/**
 * Receipt card layout as pure data (1080×1920, 9:16). The canvas renderer just
 * paints these ops, so legibility rules are unit-testable: every text ≥ 48px and
 * every line fits inside the safe width by a conservative glyph-width estimate.
 */
import { formatClock, type Receipt } from '@clutch/core';
import { isProb, pct } from '../room/derive';
import { teamAccent } from '../ui/color';

export const W = 1080;
export const H = 1920;
export const PAD = 80;
export const SAFE_W = W - PAD * 2;
export const MIN_TEXT = 48;

export type FontKind = 'display' | 'body' | 'mono';
export const FONT_FAMILY: Record<FontKind, string> = {
  display: '"Big Shoulders Display", "Arial Narrow", sans-serif',
  body: 'Barlow, "Helvetica Neue", Arial, sans-serif',
  mono: '"IBM Plex Mono", ui-monospace, monospace',
};
/** Conservative average advance per em (uppercase-heavy text) for each family. */
export const EM_WIDTH: Record<FontKind, number> = { display: 0.56, body: 0.6, mono: 0.62 };

export type TextOp = {
  kind: 'text';
  text: string;
  x: number;
  y: number; // alphabetic baseline
  size: number;
  font: FontKind;
  weight: number;
  color: string;
  align: 'left' | 'right';
};
export type RectOp = { kind: 'rect'; x: number; y: number; w: number; h: number; color: string };
export type DotOp = { kind: 'dot'; x: number; y: number; r: number; color: string; filled: boolean };
export type ReceiptOp = TextOp | RectOp | DotOp;
export type ReceiptLayout = { width: number; height: number; background: string; ops: ReceiptOp[] };

export const COLORS = {
  bg: '#0B0B0D',
  text: '#F5F3EE',
  muted: '#A3A3AD',
  volt: '#D4FF3A',
  warm: '#FFB27A',
  line: '#2A2A31',
};

export function estimateWidth(text: string, size: number, font: FontKind): number {
  return [...text].length * size * EM_WIDTH[font];
}

/** Largest size in [min, max] at which `text` fits `width` (falls back to `min`). */
export function fitSize(text: string, font: FontKind, max: number, min: number, width = SAFE_W): number {
  for (let s = max; s > min; s -= 2) if (estimateWidth(text, s, font) <= width) return s;
  return min;
}

/** Greedy word wrap by estimated width; at most `maxLines` (last line ellipsized). */
export function wrap(text: string, size: number, font: FontKind, width = SAFE_W, maxLines = 3): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const cand = cur ? `${cur} ${w}` : w;
    if (estimateWidth(cand, size, font) <= width || !cur) cur = cand;
    else {
      lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  let last = kept[maxLines - 1]!;
  while (last.length > 1 && estimateWidth(`${last}…`, size, font) > width) last = last.slice(0, -1);
  kept[maxLines - 1] = `${last.trimEnd()}…`;
  return kept;
}

export function matchupTricodes(matchup: string): { away: string; home: string } | undefined {
  const m = /^([A-Z]{3})\s*@\s*([A-Z]{3})$/.exec(matchup.trim());
  return m ? { away: m[1]!, home: m[2]! } : undefined;
}

export const FOOTER = 'Clutch · replay of real NBA play-by-play';

export function receiptLayout(r: Receipt, opts: { choiceColor?: string } = {}): ReceiptLayout {
  const ops: ReceiptOp[] = [];
  const t = (text: string, y: number, size: number, font: FontKind, weight: number, color: string, align: 'left' | 'right' = 'left') =>
    ops.push({ kind: 'text', text, x: align === 'left' ? PAD : W - PAD, y, size, font, weight, color, align });

  const teams = matchupTricodes(r.matchup);
  const awayC = teams ? teamAccent(teams.away) : COLORS.muted;
  const homeC = teams ? teamAccent(teams.home) : COLORS.muted;
  ops.push({ kind: 'rect', x: 0, y: 0, w: W / 2, h: 20, color: awayC });
  ops.push({ kind: 'rect', x: W / 2, y: 0, w: W / 2, h: 20, color: homeC });

  // Brand row
  t('CLUTCH', 150, 64, 'display', 900, COLORS.volt);
  t('RECEIPT', 146, 48, 'mono', 500, COLORS.muted, 'right');

  // Game
  t(`FINAL · ${r.matchup}`, 270, 52, 'mono', 500, COLORS.muted);
  const scoreSize = fitSize(r.finalScore, 'display', 132, 72);
  t(r.finalScore, 270 + 24 + scoreSize, scoreSize, 'display', 900, COLORS.text);
  let y = 270 + 24 + scoreSize + 56;
  ops.push({ kind: 'rect', x: PAD, y, w: SAFE_W, h: 3, color: COLORS.line });

  // Question
  y += 90;
  t('THE CALL', y, 48, 'mono', 500, COLORS.muted);
  for (const line of wrap(r.question, 64, 'body', SAFE_W, 2)) {
    y += 78;
    t(line, y, 64, 'body', 600, COLORS.text);
  }

  // My call
  y += 110;
  t(`${r.displayName.toUpperCase()}’S CALL`.slice(0, 28), y, 48, 'mono', 500, COLORS.muted);
  const choiceSize = fitSize(r.choiceLabel.toUpperCase(), 'display', 150, 80);
  y += choiceSize + 6;
  t(r.choiceLabel.toUpperCase(), y, choiceSize, 'display', 900, opts.choiceColor ?? COLORS.text);
  const lockBits = [`Locked ${formatClock(r.lockedAt.period, r.lockedAt.clockSec)}`];
  if (isProb(r.pChoiceAtLock)) lockBits.push(`${pct(r.pChoiceAtLock)}% chance`);
  y += 76;
  t(lockBits.join(' · '), y, fitSize(lockBits.join(' · '), 'body', 56, 48), 'body', 500, COLORS.text);

  // Result
  const result = r.result === 'correct' ? 'CALLED IT' : r.result === 'incorrect' ? 'BOLD CALL' : 'NO RESULT';
  const resultColor = r.result === 'correct' ? COLORS.volt : r.result === 'incorrect' ? COLORS.warm : COLORS.muted;
  const resultSize = fitSize(result, 'display', 190, 96);
  y += 70 + resultSize;
  t(result, y, resultSize, 'display', 900, resultColor);
  if (r.result === 'correct' && isProb(r.pChoiceAtLock) && r.pChoiceAtLock < 0.5) {
    y += 72;
    t(`Beat a ${pct(r.pChoiceAtLock)}% shot`, y, 56, 'body', 600, COLORS.text);
  }

  // Room calls
  y += 100;
  ops.push({ kind: 'rect', x: PAD, y: y - 60, w: SAFE_W, h: 3, color: COLORS.line });
  y += 10;
  t('THE ROOM', y, 48, 'mono', 500, COLORS.muted);
  const maxRows = Math.max(0, Math.floor((H - 200 - y) / 72) - (r.roomCalls.length > 1 ? 1 : 0));
  const rows = r.roomCalls.slice(0, Math.min(5, maxRows));
  for (const c of rows) {
    y += 72;
    ops.push({ kind: 'dot', x: PAD + 14, y: y - 18, r: 14, color: c.correct ? COLORS.volt : COLORS.muted, filled: c.correct });
    const name = c.displayName.length > 14 ? `${c.displayName.slice(0, 13)}…` : c.displayName;
    ops.push({ kind: 'text', text: name, x: PAD + 50, y, size: 52, font: 'body', weight: 600, color: COLORS.text, align: 'left' });
    t(c.choiceLabel, y, 52, 'body', 500, c.correct ? COLORS.volt : COLORS.muted, 'right');
  }
  const more = r.roomCalls.length - rows.length;
  if (more > 0) {
    y += 64;
    t(`+${more} more`, y, 48, 'body', 500, COLORS.muted);
  }

  // Footer (honesty line)
  const footer = wrap(FOOTER, 48, 'body', SAFE_W, 2);
  footer.forEach((line, i) => t(line, H - 80 - (footer.length - 1 - i) * 60, 48, 'body', 500, COLORS.muted));
  return { width: W, height: H, background: COLORS.bg, ops };
}
